import { AxiosError } from 'axios';
import { useQuery, UseQueryResult, UseQueryOptions } from '@tanstack/react-query';
import { couponQueryKeys } from '../../../constants/queryKeys/coupon';
import { CouponApiV2 } from '../../../lib/api/coupon/CouponApiV2';

type IssueHistoryDetailResponse = Awaited<ReturnType<typeof CouponApiV2.getCouponIssueHistoryDetail>>;

type IssueHistoryDetailOption = {
  couponIssueHistoryId: number;
  filterOption: Record<string, unknown>;
  sortOption: Record<string, unknown>;
  pagination: { pageNum: number; pageSize: number };
};

type IssueHistoryDetailData = {
  couponIssueHistoryInfo: Record<string, unknown>;
  couponIssueHistoryItemList: {
    list: Record<string, unknown>[];
    filterOption: Record<string, unknown>;
    sortOption: Record<string, unknown>;
    pagination: { pageNum: number; pageSize: number; pages: number; totalCnt: number };
  };
};

export const useCouponIssueHistoryDetailQuery = (
  option: IssueHistoryDetailOption,
  options?: UseQueryOptions<IssueHistoryDetailResponse, AxiosError, IssueHistoryDetailData>,
): UseQueryResult<IssueHistoryDetailData, AxiosError> => {
  return useQuery(
    couponQueryKeys.couponIssueHistoryDetail(option.couponIssueHistoryId, {
      filterOption: option.filterOption,
      sortOption: option.sortOption,
      pagination: option.pagination,
    }),
    () => CouponApiV2.getCouponIssueHistoryDetail({
      couponIssueHistoryId: option.couponIssueHistoryId,
      filterOption: option.filterOption,
      sortOption: option.sortOption,
      pagination: option.pagination,
    }),
    {
      select: (res: IssueHistoryDetailResponse) => {
        const totalCnt = res.data.totalCnt ?? 0;
        return {
          couponIssueHistoryInfo: res.data.couponIssueHistoryInfo ?? {},
          couponIssueHistoryItemList: {
            list: res.data.couponIssueHistoryItemList ?? [],
            filterOption: option.filterOption,
            sortOption: option.sortOption,
            pagination: {
              pageNum: option.pagination.pageNum,
              pageSize: option.pagination.pageSize,
              pages: Math.ceil(totalCnt / Math.max(1, option.pagination.pageSize)) || 1,
              totalCnt,
            },
          },
        };
      },
      enabled: !!option.couponIssueHistoryId,
      ...options,
    },
  );
};
